<template>
  <v-card width="350px" :hover="draggable">
    <v-img
      :height="resource.resourceData.type == 'image' ? '200px' : '100px'"
      v-if="resource.resourceData.thumbnailUrl || resource.resourceData.image"
      :src="resource.resourceData.thumbnailUrl || resource.resourceData.image"
    ></v-img>
    <slot></slot>
    <v-card-title
      class="grey--text text--darken-4"
      style="font-size: 1.3em; word-break: initial;"
      @click.stop.prevent
    >
      <div v-if="!userCanEdit" v-text="resource.text" class="ql-editor"></div>
      <content-editor
        v-else
        v-model="resourceText"
        ref="editor"
        :emitText="true"
        :key="resource.id"
        :placeholder="'Add a title'"
        :hijackPaste="false"
        allowTraversal
        @keyEnter=""
        @focus="focused = true"
        @blur="focused = false"
        @click.stop
      ></content-editor>
    </v-card-title>

    <v-card-subtitle class="grey--text text--darken-3 pb-0" style="font-size: 1em;">
      <div v-if="!userCanEdit" v-text="resource.description" class="ql-editor"></div>
      <content-editor
        v-else
        v-model="resourceDescription"
        ref="editor"
        :emitText="true"
        :key="resource.id"
        :placeholder="'Add a description'"
        :hijackPaste="false"
        allowTraversal
        @keyEnter=""
        @focus="focused = true"
        @blur="focused = false"
      ></content-editor>
    </v-card-subtitle>
    <v-rating v-model="resourceRating" small class="pl-3"></v-rating>
    <v-divider></v-divider>
    <v-list dense v-if="resource.resourceData.type == 'webpage'">
      <v-list-item>
        <v-list-item-icon>
          <v-avatar size="24">
            <v-img :src="resource.resourceData.icon"></v-img>
          </v-avatar>
        </v-list-item-icon>
        <v-list-item-title v-text="resource.resourceData.title"></v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>http</v-icon>
        </v-list-item-icon>
        <v-list-item-title>
          <a target="_blank" :href="resource.resourceData.url">{{ resource.resourceData.url }}</a>
        </v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>domain</v-icon>
        </v-list-item-icon>
        <v-list-item-title v-text="resource.resourceData.provider"></v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>today</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Captured {{ resource.createdOn | formatDate }}</v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>domain</v-icon>
        </v-list-item-icon>
        <v-list-item-title>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on"
                >Used on {{ Object.keys(resource.usedOn).length }} Context{{
                  Object.keys(resource.usedOn).length == 1 ? '' : 's'
                }}</span
              >
            </template>
            <div v-for="item in resource.usedOn" :key="item.id">{{ item.text }}</div>
          </v-tooltip>
        </v-list-item-title>
      </v-list-item>
    </v-list>
    <v-list dense v-if="resource.resourceData.type == 'pdf'">
      <v-list-item>
        <v-list-item-icon>
          <v-icon>picture_as_pdf</v-icon>
        </v-list-item-icon>
        <v-list-item-title v-text="resource.resourceData.name"></v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>today</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Uploaded {{ resource.createdOn | formatDate }}</v-list-item-title>
      </v-list-item>
    </v-list>
    <v-list dense v-if="resource.resourceData.type == 'image'">
      <v-list-item>
        <v-list-item-icon>
          <v-icon>image</v-icon>
        </v-list-item-icon>
        <v-list-item-title v-text="resource.resourceData.name"></v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>today</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Uploaded {{ resource.createdOn | formatDate }}</v-list-item-title>
      </v-list-item>
      <v-list-item>
        <v-list-item-icon>
          <v-icon>get_app</v-icon>
        </v-list-item-icon>
        <v-list-item-title
          ><a :href="resource.resourceData.downloadUrl" target="_blank"
            >Download Image</a
          ></v-list-item-title
        >
      </v-list-item>
    </v-list>
    <v-divider></v-divider>
    <v-card-actions v-if="showOpen"
      ><v-btn
        text
        @click="
          setResourceViewer({ show: true, resourceId: resource.id });
          $emit('close');
        "
        >Open Resource</v-btn
      ></v-card-actions
    >
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ContentEditor from '@/components/ContentEditor';
export default {
  props: {
    resource: {},
    showOpen: { default: false, type: Boolean },
    draggable: { default: false, type: Boolean }
  },
  components: { ContentEditor },
  data() {
    return {};
  },
  computed: {
    ...mapGetters('userWorkspaceLite', ['workspaceResources']),
    userCanEdit() {
      return !!this.workspaceResources.find(r => r.id == this.resource.id);
    },
    resourceText: {
      get() {
        return { html: this.resource.text };
      },
      set(v) {
        this.updateResourceText({
          resourceId: this.resource.id,
          text: v
        });
      }
    },
    resourceDescription: {
      get() {
        return { html: this.resource.description };
      },
      set(v) {
        this.updateResourceText({
          resourceId: this.resource.id,
          text: v,
          field: 'description'
        });
      }
    },
    resourceRating: {
      get() {
        return this.resource.rating || 0;
      },
      set(v) {
        this.updateResourceText({
          resourceId: this.resource.id,
          text: v,
          field: 'rating'
        });
      }
    }
  },
  methods: {
    ...mapActions('resources', ['updateResourceText']),
    ...mapMutations('views/context', ['setResourceViewer'])
  }
};
</script>

<style></style>
